/**
 * Talksy Logo Component
 * Feature: Branding
 *
 * Signal Flow Logo - 4 vertical bars representing sound waves
 * Based on original branding resource from new-ui-samples/branding-logo
 * 
 * Colors:
 * - Indigo (#6366F1)
 * - Violet (#885CF6)
 * - Teal (#2DD4BF)
 * - Teal/60 (rgba(45, 212, 191, 0.6))
 * 
 * Bar heights ratio (based on original h-8 container):
 * - Bar 1: 62.5% (h-5/h-8)
 * - Bar 2: 100% (h-8/h-8) 
 * - Bar 3: 87.5% (h-7/h-8)
 * - Bar 4: 50% (h-4/h-8)
 */

interface TalksyLogoProps {
  /** Size variant */
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  /** Show text alongside icon */
  showText?: boolean;
  /** Enable loading animation */
  animated?: boolean;
  /** Custom className */
  className?: string;
}

// Size configurations matching logo-horizontal.svg
// SVG viewBox: 280x48, bars: width=8, gap=4, heights: 30, 48, 42, 24
// Ratios based on container height 48: width=8/48=1/6, gap=4/48=1/12
// Height ratios: 30/48=62.5%, 48/48=100%, 42/48=87.5%, 24/48=50%
const sizeConfig = {
  xs: {
    container: 'h-4',          // 16px
    gap: 'gap-[1px]',          // 16/12 ≈ 1px
    barWidth: 'w-[3px]',       // 16/6 ≈ 3px
    heights: ['h-[10px]', 'h-4', 'h-[14px]', 'h-2'],  // 10, 16, 14, 8
    text: 'text-sm',
    textGap: 'gap-2',
  },
  sm: {
    container: 'h-8',          // 32px
    gap: 'gap-[3px]',          // 32/12 ≈ 3px (was gap-1.5 = 6px)
    barWidth: 'w-[5px]',       // 32/6 ≈ 5px (was w-2 = 8px)
    heights: ['h-5', 'h-8', 'h-7', 'h-4'],  // 20, 32, 28, 16
    text: 'text-xl',
    textGap: 'gap-3',          // 16px gap like SVG (60-44=16 scaled)
  },
  md: {
    container: 'h-12',         // 48px (matches SVG viewBox height)
    gap: 'gap-1',              // 4px
    barWidth: 'w-2',           // 8px
    heights: ['h-[30px]', 'h-12', 'h-[42px]', 'h-6'],  // 30, 48, 42, 24
    text: 'text-2xl',
    textGap: 'gap-4',
  },
  lg: {
    container: 'h-16',         // 64px
    gap: 'gap-[5px]',          // 64/12 ≈ 5px
    barWidth: 'w-[11px]',      // 64/6 ≈ 11px
    heights: ['h-10', 'h-16', 'h-14', 'h-8'],  // 40, 64, 56, 32
    text: 'text-4xl',
    textGap: 'gap-6',
  },
  xl: {
    container: 'h-24',         // 96px
    gap: 'gap-2',              // 8px
    barWidth: 'w-4',           // 16px
    heights: ['h-[60px]', 'h-24', 'h-[84px]', 'h-12'],  // 60, 96, 84, 48
    text: 'text-6xl',
    textGap: 'gap-8',
  },
} as const;

// Bar colors in order
const barColors = [
  'bg-[#6366F1]',      // Indigo
  'bg-[#885CF6]',      // Violet
  'bg-[#2DD4BF]',      // Teal
  'bg-[#2DD4BF]/60',   // Teal 60%
];

// Animation classes for loading state
const animationClasses = [
  'animate-signal-1',
  'animate-signal-2',
  'animate-signal-3',
  'animate-signal-4',
];

export function TalksyLogo({
  size = 'sm',
  showText = false,
  animated = false,
  className = '',
}: TalksyLogoProps) {
  const config = sizeConfig[size];

  return (
    <div className={`flex items-center ${showText ? config.textGap : ''} ${className}`}>
      {/* Signal Flow Icon - 4 bars */}
      <div className={`flex items-center ${config.gap} ${config.container}`}>
        {barColors.map((color, index) => (
          <div
            key={index}
            className={`
              ${config.barWidth}
              ${config.heights[index]}
              rounded-full
              ${color}
              ${animated ? animationClasses[index] : ''}
            `}
          />
        ))}
      </div>
      
      {/* Talksy Text */}
      {showText && (
        <span className={`${config.text} font-extrabold tracking-tight text-white`}>
          Talksy
        </span>
      )}
    </div>
  );
}

/**
 * Talksy Icon Only (for favicon, app icon usage)
 */
export function TalksyIcon({
  size = 'sm',
  animated = false,
  className = '',
}: Omit<TalksyLogoProps, 'showText'>) {
  return (
    <TalksyLogo
      size={size}
      showText={false}
      animated={animated}
      className={className}
    />
  );
}

/**
 * Talksy Full Logo (icon + text)
 * For headers and branding areas
 */
export function TalksyFullLogo({
  size = 'sm',
  animated = false,
  className = '',
}: Omit<TalksyLogoProps, 'showText'>) {
  return (
    <TalksyLogo
      size={size}
      showText={true}
      animated={animated}
      className={className}
    />
  );
}

/**
 * Talksy Text Only
 * For flexible layout where icon and text need separate positioning
 */
export function TalksyText({
  size = 'sm',
  className = '',
}: {
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
}) {
  const config = sizeConfig[size];
  return (
    <span className={`${config.text} font-extrabold tracking-tight text-white ${className}`}>
      Talksy
    </span>
  );
}

/**
 * Loading Spinner using Signal Flow
 * Animated 4-bar signal flow for loading states
 * Text-free, minimal design
 */
interface LoadingSpinnerProps {
  /** Size of the spinner */
  size?: 'sm' | 'md' | 'lg';
  /** Custom className */
  className?: string;
}

export function SignalFlowSpinner({
  size = 'md',
  className = '',
}: LoadingSpinnerProps) {
  // All bars have SAME height (like original code.html loading animation)
  // Animation: 1s loop, scaleY 0.5 -> 1
  const spinnerSizes = {
    sm: { 
      container: 'h-6',        // 24px
      gap: 'gap-0.5',          // 2px
      barWidth: 'w-1',         // 4px
      barHeight: 'h-6',        // All same height
    },
    md: { 
      container: 'h-12',       // 48px
      gap: 'gap-1',            // 4px
      barWidth: 'w-2',         // 8px
      barHeight: 'h-12',       // All same height
    },
    lg: { 
      container: 'h-16',       // 64px
      gap: 'gap-[5px]',        // 5px
      barWidth: 'w-[11px]',    // 11px
      barHeight: 'h-16',       // All same height
    },
  };

  const config = spinnerSizes[size];

  return (
    <div className={`flex items-center ${config.gap} ${config.container} ${className}`}>
      <div className={`${config.barWidth} ${config.barHeight} rounded-full bg-[#6366F1] animate-signal-1`} />
      <div className={`${config.barWidth} ${config.barHeight} rounded-full bg-[#885CF6] animate-signal-2`} />
      <div className={`${config.barWidth} ${config.barHeight} rounded-full bg-[#2DD4BF] animate-signal-3`} />
      <div className={`${config.barWidth} ${config.barHeight} rounded-full bg-[#2DD4BF]/60 animate-signal-4`} />
    </div>
  );
}

export default TalksyLogo;
